import '../styles/pages.scss';
import {useEffect, useRef, useState, useMemo, useCallback, useTransition} from "react";
import {useLocation} from 'react-router-dom';
import {getRecordsByDate, getStrategies} from "../lib/beCall";
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Select,
    Spinner,
    Center,
    Text
} from "@chakra-ui/react";
import {SearchIcon} from "@chakra-ui/icons";
import Toolbar from "../components/toolbar/toolbar";
import OfflineScreen from "../components/offlineScreen/offlineScreen";
import {formatDataItalianoConOra, formatOra} from "../utilities/formatDate";
import {formatStrategyName} from "../utilities/formatStrategyName";
import {useDate} from "../contexts/DateContext";

const RecordsByDate = () => {
    const location = useLocation();
    const {
        selectedDate: lastDay,
        setSelectedDate: setLastDay,
        isPolling,
        setIsPolling,
        consecutiveErrors,
        setConsecutiveErrors,
        hasConnectedOnce,
        setHasConnectedOnce
    } = useDate();
    const MAX_CONSECUTIVE_ERRORS = 50;
    const [strategies, setStrategies] = useState([]);
    const [records, setRecords] = useState([]);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [selectedSeverity, setSelectedSeverity] = useState("");
    const [selectedStrategy, setSelectedStrategy] = useState("");
    const [selectedFilterIndex, setSelectedFilterIndex] = useState("");
    const [limit, setLimit] = useState(100);
    const [isOffline, setIsOffline] = useState(false);
    const [nextRetryIn, setNextRetryIn] = useState(0);
    const retryTimeoutRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const intervalRef = useRef(null);
    const isPollingRef = useRef(isPolling);
    const abortControllerRef = useRef(null);
    const debounceTimeoutRef = useRef(null);
    const consecutiveErrorsRef = useRef(consecutiveErrors);
    const hasConnectedOnceRef = useRef(hasConnectedOnce);
    const [isPending, startTransition] = useTransition();
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        isPollingRef.current = isPolling;
    }, [isPolling]);

    useEffect(() => {
        consecutiveErrorsRef.current = consecutiveErrors;
    }, [consecutiveErrors]);

    useEffect(() => {
        hasConnectedOnceRef.current = hasConnectedOnce;
    }, [hasConnectedOnce]);

    useEffect(() => {
        loadInitialData();
    }, []);

    // Handler per la ricerca con debounce
    const handleSearchChange = useCallback((value) => {
        setIsSearching(true);

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            startTransition(() => {
                setDebouncedSearchTerm(value);
                setLimit(100);
                setIsSearching(false);
            });
        }, 500);
    }, []);

    const loadInitialData = async () => {
        try {
            const data = await getStrategies();
            if (data) {
                // Setta la data SOLO se non è già presente nel Context
                if (!lastDay) {
                    setLastDay(data['date']);
                }
                setStrategies(data['strategies'] || []);
                setIsOffline(false);
            } else {
                handleOffline();
            }
        } catch (error) {
            console.error('Failed to load initial data:', error);
            handleOffline();
        }
    };

    const handleOffline = () => {
        setIsOffline(true);
        scheduleRetry();
    };

    const handleOnline = () => {
        consecutiveErrorsRef.current = 0;
        setConsecutiveErrors(0);
        hasConnectedOnceRef.current = true;
        setHasConnectedOnce(true);
        setIsOffline(false);
        clearRetryTimeout();
    };

    const scheduleRetry = () => {
        clearRetryTimeout();

        setNextRetryIn(30);
        countdownIntervalRef.current = setInterval(() => {
            setNextRetryIn(prev => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        retryTimeoutRef.current = setTimeout(() => {
            loadInitialData();
        }, 30000);
    };

    const clearRetryTimeout = () => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setNextRetryIn(0);
    };

    const handleManualRetry = () => {
        clearRetryTimeout();
        loadInitialData();
    };

    useEffect(() => {
        const clearCurrentInterval = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const abortCurrentRequests = () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };

        clearCurrentInterval();
        abortCurrentRequests();

        if (!lastDay) return;

        const pollingFn = async () => {
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            try {
                const data = await getRecordsByDate(lastDay, signal);
                if (data) {
                    if (data.length > 0) {
                        const sortedData = [...data].sort((a, b) => {
                            const dateA = new Date(a.ts_event_iso);
                            const dateB = new Date(b.ts_event_iso);
                            return dateB - dateA;
                        });
                        setRecords(sortedData);
                    } else {
                        // Array vuoto - nessun record per questa data
                        setRecords([]);
                    }
                    handleOnline();
                }
            } catch (error) {
                if (error.name === 'CanceledError' || error.name === 'AbortError') {
                    console.log('Polling requests canceled');
                } else {
                    // Se non c'è mai stata una connessione riuscita, vai offline subito
                    if (!hasConnectedOnceRef.current) {
                        console.error('Error in polling (server not available):', error);
                        handleOffline();
                        return;
                    }

                    // Altrimenti usa il sistema di protezione
                    consecutiveErrorsRef.current += 1;
                    setConsecutiveErrors(consecutiveErrorsRef.current);
                    console.error(`Error in polling (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS}):`, error);

                    if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
                        handleOffline();
                    }
                }
            }
        };

        pollingFn().then(() => {
            // Avvia il polling continuo SOLO se isPolling è true E siamo sulla pagina /logs
            if (isPollingRef.current && !intervalRef.current && location.pathname === '/logs') {
                intervalRef.current = setInterval(pollingFn, 3000);
            }
        }).catch((error) => {
            if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
                console.error('Error in initial polling:', error);
            }
        });

        return () => {
            clearCurrentInterval();
            abortCurrentRequests();
        };
    }, [lastDay, isPolling, location.pathname]);

    // Memoizza il filtraggio per evitare calcoli inutili
    const filteredRecords = useMemo(() => {
        let filtered = records;

        // Filtro per search term
        if (debouncedSearchTerm) {
            const searchLower = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(record => {
                return (
                    record.msg?.toLowerCase().includes(searchLower) ||
                    record.severity?.toLowerCase().includes(searchLower) ||
                    record.strategy?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Filtro per severity
        if (selectedSeverity) {
            filtered = filtered.filter(record =>
                record.severity?.toUpperCase() === selectedSeverity
            );
        }

        // Filtro per strategy
        if (selectedStrategy) {
            filtered = filtered.filter(record =>
                record.strategy === selectedStrategy
            );
        }

        // Filtro per filter_index
        if (selectedFilterIndex) {
            filtered = filtered.filter(record =>
                record.filter_index === parseInt(selectedFilterIndex)
            );
        }

        return filtered;
    }, [debouncedSearchTerm, selectedSeverity, selectedStrategy, selectedFilterIndex, records]);

    // Memoizza i record da visualizzare
    const displayedRecords = useMemo(() => {
        return filteredRecords.slice(0, limit);
    }, [filteredRecords, limit]);

    const tableContainerRef = useRef(null);

    // Usa useCallback per evitare ricreazioni della funzione
    const handleScroll = useCallback((e) => {
        const {scrollTop, scrollHeight, clientHeight} = e.target;
        if (scrollHeight - scrollTop <= clientHeight * 1.5 && limit < filteredRecords.length) {
            setLimit(prev => Math.min(prev + 100, filteredRecords.length));
        }
    }, [limit, filteredRecords.length]);

    // Funzione per estrarre i campi extra (esclusi quelli fissi)
    const getExtraFields = useCallback((row) => {
        const fixedFields = ['msg', 'severity', 'strategy', 'ts_event', 'ts_event_iso'];
        const extraFields = {};

        Object.keys(row).forEach(key => {
            if (!fixedFields.includes(key) && row[key] !== null && row[key] !== undefined && row[key] !== '') {
                extraFields[key] = row[key];
            }
        });

        return extraFields;
    }, []);

    useEffect(() => {
        return () => {
            clearRetryTimeout();
        };
    }, []);

    if (isOffline) {
        return <OfflineScreen onRetry={handleManualRetry} nextRetryIn={nextRetryIn}/>;
    }

    // Determina se mostrare lo spinner: se abbiamo una data ma nessun record ancora
    const isLoadingData = lastDay && records.length === 0 && !isOffline;

    return (
        <Flex direction="column" h="100vh" overflow="hidden">
            <Box>
                <Toolbar last_day={lastDay} setDate={setLastDay}/>
            </Box>
            <Box flex="1" overflow="hidden" p={{base: 2, md: 4}} display="flex" flexDirection="column">
                <Flex mb={2} gap={3} direction={{base: "column", md: "row"}} alignItems={{base: "stretch", md: "flex-start"}}>
                    <InputGroup maxW={{base: "100%", md: "400px"}}>
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.300" />
                        </InputLeftElement>
                        <Input
                            placeholder="Cerca per messaggio..."
                            defaultValue=""
                            onChange={(e) => handleSearchChange(e.target.value)}
                            bg="white"
                            borderColor="gray.300"
                            _hover={{borderColor: "gray.400"}}
                            _focus={{borderColor: "#38A169", boxShadow: "0 0 0 1px #38A169"}}
                            size={{base: "sm", md: "md"}}
                        />
                        {(isSearching || isPending) && (
                            <InputRightElement>
                                <Spinner size="sm" color="#38A169" thickness="2px" />
                            </InputRightElement>
                        )}
                    </InputGroup>

                    <Flex gap={3} direction={{base: "column", sm: "row"}} flex={{base: "1", md: "0"}}>
                        <Select
                            placeholder="Tutte le Severity"
                            value={selectedSeverity}
                            onChange={(e) => setSelectedSeverity(e.target.value)}
                            maxW={{base: "100%", sm: "200px"}}
                            minW={{base: "auto", md: "180px"}}
                            bg="white"
                            borderColor="gray.300"
                            _hover={{borderColor: "gray.400"}}
                            _focus={{borderColor: "#38A169", boxShadow: "0 0 0 1px #38A169"}}
                            size={{base: "sm", md: "md"}}
                            icon={<Box as="span" ml={2}>▼</Box>}
                        >
                            <option value="">Tutte le Severity</option>
                            <option value="ERROR">ERROR</option>
                            <option value="DEBUG">DEBUG</option>
                            <option value="INFO">INFO</option>
                            <option value="WARNING">WARNING</option>
                        </Select>

                        <Select
                            placeholder="Tutte le Strategy"
                            value={selectedStrategy}
                            onChange={(e) => setSelectedStrategy(e.target.value)}
                            maxW={{base: "100%", sm: "250px"}}
                            minW={{base: "auto", md: "200px"}}
                            bg="white"
                            borderColor="gray.300"
                            _hover={{borderColor: "gray.400"}}
                            _focus={{borderColor: "#38A169", boxShadow: "0 0 0 1px #38A169"}}
                            size={{base: "sm", md: "md"}}
                            icon={<Box as="span" ml={2}>▼</Box>}
                        >
                            <option value="">Tutte le Strategy</option>
                            {strategies.map((strategy, index) => (
                                <option key={index} value={strategy}>
                                    {formatStrategyName(strategy)}
                                </option>
                            ))}
                        </Select>

                        <Select
                            placeholder="Filter Index"
                            value={selectedFilterIndex}
                            onChange={(e) => setSelectedFilterIndex(e.target.value)}
                            maxW={{base: "100%", sm: "300px"}}
                            minW={{base: "auto", md: "250px"}}
                            bg="white"
                            borderColor="gray.300"
                            _hover={{borderColor: "gray.400"}}
                            _focus={{borderColor: "#38A169", boxShadow: "0 0 0 1px #38A169"}}
                            size={{base: "sm", md: "md"}}
                            icon={<Box as="span" ml={2}>▼</Box>}
                        >
                            <option value="">Tutti</option>
                            <option value="1">1: volume vs media</option>
                            <option value="2">2: inside bar (struttura)</option>
                            <option value="3">3: range relativo della barra</option>
                            <option value="4">4: spread bid/ask non valutabile (NaN)</option>
                            <option value="5">5: spread bid/ask sopra/sotto soglia</option>
                            <option value="6">6: volume in USD &gt; 0</option>
                            <option value="7">7: rapporto impegno finanziario / volume</option>
                            <option value="8">8: commissioni broker (IBKR Fixed) entro limite</option>
                            <option value="9">9: cluster ML ammesso</option>
                            <option value="10">10: larghezza minima inside bar</option>
                            <option value="11">11: gap di apertura dentro [min_gap, max_gap]</option>
                            <option value="12">12: market cap dentro range (o filtro disattivato)</option>
                        </Select>
                    </Flex>
                </Flex>

                <Box fontSize="sm" color="gray.600" mb={2} px={{base: 0, md: 1}}>
                    Mostrando {displayedRecords.length} di {filteredRecords.length} record
                </Box>

                <TableContainer
                    ref={tableContainerRef}
                    flex="1"
                    overflowY="auto"
                    onScroll={handleScroll}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#555',
                        },
                    }}
                >
                    {isLoadingData ? (
                        <Center h="400px">
                            <Flex direction="column" align="center" gap={4}>
                                <Spinner
                                    thickness="4px"
                                    speed="0.65s"
                                    emptyColor="gray.200"
                                    color="#38A169"
                                    size="xl"
                                />
                                <Text color="gray.600" fontSize="md">Caricamento dati in corso...</Text>
                            </Flex>
                        </Center>
                    ) : (
                        <Table variant='simple' size={{base: "sm", md: "sm"}}>
                            <Thead position="sticky" top={0} bg="white" zIndex={1}>
                                <Tr>
                                    <Th width={{base: "100%", lg: "50%"}} fontSize={{base: "xs", md: "sm"}}>Logs</Th>
                                    <Th width="15%" display={{base: "none", lg: "table-cell"}} fontSize="sm">Severity</Th>
                                    <Th width="15%" display={{base: "none", lg: "table-cell"}} fontSize="sm">Strategy</Th>
                                    <Th width="20%" display={{base: "none", lg: "table-cell"}} fontSize="sm">Ora</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {displayedRecords.map((row, index) => {
                                    const extraFields = getExtraFields(row);
                                    const hasExtraFields = Object.keys(extraFields).length > 0;

                                    return (
                                        <Tr key={index} _hover={{bg: "gray.50"}}>
                                            <Td whiteSpace="normal" wordBreak="break-word" fontSize={{base: "xs", md: "sm"}}>
                                                {row.msg || '-'}

                                                {/* Mobile e Tablet portrait: mostra severity, strategy, data e campi extra sotto il messaggio */}
                                                <Box display={{base: "flex", lg: "none"}} mt={2} gap={3} fontSize="xs" color="gray.600" flexWrap="wrap">
                                                    <Box bg="gray.100" px={2} py={1} borderRadius="md">
                                                        <strong>Severity:</strong> {row.severity || '-'}
                                                    </Box>
                                                    <Box bg="gray.100" px={2} py={1} borderRadius="md">
                                                        <strong>Strategy:</strong> {formatStrategyName(row.strategy) || '-'}
                                                    </Box>
                                                    <Box bg="gray.100" px={2} py={1} borderRadius="md">
                                                        <strong>Ora:</strong> {row.ts_event_iso ? formatOra(row.ts_event_iso) : '-'}
                                                    </Box>
                                                    {/* Campi extra su mobile */}
                                                    {hasExtraFields && Object.entries(extraFields).map(([key, value]) => (
                                                        <Box key={key} bg="blue.50" px={2} py={1} borderRadius="md">
                                                            <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value}
                                                        </Box>
                                                    ))}
                                                </Box>

                                                {/* Desktop: mostra campi extra sotto il messaggio */}
                                                {hasExtraFields && (
                                                    <Box display={{base: "none", lg: "flex"}} mt={2} gap={2} fontSize="xs" color="gray.600" flexWrap="wrap">
                                                        {Object.entries(extraFields).map(([key, value]) => (
                                                            <Box key={key} bg="blue.50" px={2} py={1} borderRadius="md">
                                                                <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Td>
                                            <Td display={{base: "none", lg: "table-cell"}} fontSize="sm">{row.severity || '-'}</Td>
                                            <Td display={{base: "none", lg: "table-cell"}} fontSize="sm">{formatStrategyName(row.strategy) || '-'}</Td>
                                            <Td display={{base: "none", lg: "table-cell"}} fontSize="sm">{row.ts_event_iso ? formatOra(row.ts_event_iso) : '-'}</Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    )}
                </TableContainer>
            </Box>
        </Flex>
    );
};

export default RecordsByDate;
